import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  IconCalendar,
  IconClock,
  IconMail,
  IconMailOpened,
  IconPaperclip,
  IconTag,
  IconSettings2,
  IconCalendarWeek,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import type { CreateColumnInput, ColumnFilters } from '@/types/column';
import { fetchLabels, Label } from '@/lib/cloudflare/labels-client';

interface ColumnPresetsProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPreset: (input: CreateColumnInput) => void;
  onSelectCustom: () => void;
}

interface PresetOption {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  filters: ColumnFilters;
}

const PRESET_OPTIONS: PresetOption[] = [
  {
    id: 'today',
    name: 'Today',
    description: 'Emails received today',
    icon: <IconCalendar className="h-5 w-5" />,
    filters: {
      logic: 'AND',
      rules: [{ type: 'receivedWithin', operator: 'is', value: '1' }],
    },
  },
  {
    id: 'last-3-days',
    name: 'Last 3 Days',
    description: 'Emails from the past 3 days',
    icon: <IconCalendarWeek className="h-5 w-5" />,
    filters: {
      logic: 'AND',
      rules: [{ type: 'receivedWithin', operator: 'is', value: '3' }],
    },
  },
  {
    id: 'last-week',
    name: 'Last 7 Days',
    description: 'Emails from the past week',
    icon: <IconClock className="h-5 w-5" />,
    filters: {
      logic: 'AND',
      rules: [{ type: 'receivedWithin', operator: 'is', value: '7' }],
    },
  },
  {
    id: 'unread',
    name: 'Unread',
    description: 'All unread emails',
    icon: <IconMail className="h-5 w-5" />,
    filters: {
      logic: 'AND',
      rules: [{ type: 'isUnread', operator: 'is', value: true }],
    },
  },
  {
    id: 'with-attachments',
    name: 'Has Attachments',
    description: 'Emails with attachments',
    icon: <IconPaperclip className="h-5 w-5" />,
    filters: {
      logic: 'AND',
      rules: [{ type: 'hasAttachments', operator: 'is', value: true }],
    },
  },
];

export function ColumnPresets({
  isOpen,
  onClose,
  onSelectPreset,
  onSelectCustom,
}: ColumnPresetsProps) {
  const [userLabels, setUserLabels] = useState<Label[]>([]);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);

  useEffect(() => {
    if (isOpen && userLabels.length === 0) {
      setIsLoadingLabels(true);
      fetchLabels()
        .then(({ labels }) => setUserLabels(labels))
        .catch(err => console.error('Failed to fetch labels:', err))
        .finally(() => setIsLoadingLabels(false));
    }
  }, [isOpen]);

  const handleSelectPreset = (preset: PresetOption) => {
    onSelectPreset({
      name: preset.name,
      filters: preset.filters,
    });
    onClose();
  };

  const handleSelectLabel = (label: Label) => {
    onSelectPreset({
      name: label.name,
      filters: {
        logic: 'AND',
        rules: [{ type: 'label', operator: 'is', value: [label.name] }],
      },
    });
    onClose();
  };

  const handleSelectCustom = () => {
    onClose();
    onSelectCustom();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[400px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-lg">Add column</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {/* Time-based presets */}
          <div className="space-y-1">
            {PRESET_OPTIONS.map((preset) => (
              <button
                key={preset.id}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left"
                onClick={() => handleSelectPreset(preset)}
              >
                <span className="text-muted-foreground">{preset.icon}</span>
                <div>
                  <div className="text-sm font-medium">{preset.name}</div>
                  <div className="text-xs text-muted-foreground">{preset.description}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-border/50" />

          {/* Labels section */}
          <div className="space-y-1">
            <div className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              By Label
            </div>
            {isLoadingLabels ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">Loading labels...</div>
            ) : userLabels.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No labels found</div>
            ) : (
              userLabels.slice(0, 10).map((label) => (
                <button
                  key={label.id}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left"
                  onClick={() => handleSelectLabel(label)}
                >
                  <span
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: label.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{label.name}</div>
                  </div>
                  {label.email_count !== undefined && (
                    <span className="text-xs text-muted-foreground">{label.email_count}</span>
                  )}
                </button>
              ))
            )}
            {userLabels.length > 10 && (
              <div className="px-3 py-1 text-xs text-muted-foreground">
                +{userLabels.length - 10} more labels available in custom
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="my-3 border-t border-border/50" />

          {/* Custom option */}
          <button
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left"
            onClick={handleSelectCustom}
          >
            <span className="text-muted-foreground">
              <IconSettings2 className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-medium">Custom</div>
              <div className="text-xs text-muted-foreground">Create with custom filter rules</div>
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
